// src/components/DigitalAds.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const DigitalAds = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [creatives, setCreatives] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchAds();
    fetchCreatives();
  }, []);

  React.useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchAds = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/get-ads/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAds(data.ads || []);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreatives = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/get-creatives/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCreatives({
          images: data.images || [],
          videos: data.videos || []
        });
      }
    } catch (error) {
      console.error('Error fetching creatives:', error);
    }
  };

  const totalSpend = ads.reduce((sum, ad) => sum + (parseFloat(ad.lifetime_budget) || 0), 0);
  const activeCampaigns = ads.filter(ad => ad.status === 'active').length;
  const totalReach = ads.reduce((sum, ad) => sum + (parseInt(ad.impressions) || 0), 0);

  const adPlatforms = [
    {
      id: 'facebook',
      title: 'Facebook Advertising',
      description: 'Create and manage Facebook and Instagram ad campaigns with advanced targeting',
      icon: '📘',
      color: '#1877F2',
      status: 'Active',
      path: '/admin/digital-ads/facebook',
      metrics: { campaigns: activeCampaigns, spend: `$${totalSpend.toFixed(2)}`, reach: totalReach > 1000 ? `${(totalReach/1000).toFixed(1)}K` : totalReach.toString() }
    },
    {
      id: 'google',
      title: 'Google Ads',
      description: 'Manage Google Search, Display, and YouTube advertising campaigns',
      icon: '🔍',
      color: '#4285F4',
      status: 'Active',
      path: '/admin/digital-ads/google',
      metrics: { campaigns: 0, spend: '$0', reach: '0' }
    },
    {
      id: 'creative',
      title: 'Creative Studio',
      description: 'Design, manage, and optimize ad creatives, images, and video assets',
      icon: '🎨',
      color: '#FF6B35',
      status: 'Active',
      path: '/admin/digital-ads/creative-manager',
      metrics: {
        assets: creatives.images.length + creatives.videos.length,
        images: creatives.images.length,
        videos: creatives.videos.length
      }
    },
    {
      id: 'ai-staging',
      title: 'AI Room Staging Studio',
      description: 'Transform property images with AI-powered room staging and design',
      icon: '🤖',
      color: '#667eea',
      status: 'Active',
      path: '/admin/digital-ads/ai-staging',
      metrics: {
        rooms: 'Unlimited',
        styles: '50+',
        instant: 'Yes'
      }
    }
  ];

  return (
    <div className="admin-dashboard-modern">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Digital Advertising</h1>
            <p className="dashboard-subtitle">Manage and optimize your digital marketing campaigns</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main-content">
        {/* Overview Stats */}
        <div className="stats-section">
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#1877F2'}}>📱</div>
            <div className="stat-content">
              <div className="stat-number">{loading ? '-' : activeCampaigns}</div>
              <div className="stat-label">Active Campaigns</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#4CAF50'}}>💰</div>
            <div className="stat-content">
              <div className="stat-number">{loading ? '-' : `$${totalSpend.toFixed(2)}`}</div>
              <div className="stat-label">Total Spend</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#FF9800'}}>👁️</div>
            <div className="stat-content">
              <div className="stat-number">{loading ? '-' : totalReach > 1000 ? `${(totalReach/1000).toFixed(1)}K` : totalReach}</div>
              <div className="stat-label">Total Reach</div>
            </div>
          </div>
        </div>

        {/* Platforms Section */}
        <div className="platforms-section">
          <div className="section-header">
            <h2 className="section-title">Advertising Platforms</h2>
          </div>

          <div className="platforms-grid">
            {adPlatforms.map((platform) => (
              <div
                key={platform.id}
                className={`platform-card ${platform.status === 'Coming Soon' ? 'disabled' : ''}`}
                onClick={() => platform.path && navigate(platform.path)}
                style={{'--platform-color': platform.color}}
              >
                <div className="platform-header">
                  <div className="platform-icon" style={{backgroundColor: platform.color}}>
                    {platform.icon}
                  </div>
                  <div className={`platform-status ${platform.status === 'Active' ? 'active' : 'coming-soon'}`}>
                    {platform.status}
                  </div>
                </div>

                <div className="platform-content">
                  <h3 className="platform-title">{platform.title}</h3>
                  <p className="platform-description">{platform.description}</p>
                </div>

                <div className="platform-metrics">
                  {Object.entries(platform.metrics).map(([key, value]) => (
                    <div key={key} className="metric-item">
                      <span className="metric-value">{value}</span>
                      <span className="metric-label">{key}</span>
                    </div>
                  ))}
                </div>

                {platform.status === 'Active' && (
                  <div className="platform-footer">
                    <span className="platform-action">Manage Platform →</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DigitalAds;